/**
 * What the rate-position row says after its tiles: where the pick leaves the two rate-risk limits, the one tile that clears a flag, and what the pick does (the engine's own sentence).
 * Reads the on-screen risk projection and the draft through the decision sheet's context and the preview's trace; with no projection it renders nothing and the older lines stay.
 */
import { GxPill } from "../../theme/GxPill";
import { Fragment, type ReactNode } from "react";
import type { PreviewTrace } from "@bsim/domain";
import { GxDescribed, GxTerm } from "../../theme/GxTerm";
import { formatRate } from "../../format";
import { useDecisionSheet } from "../DecisionSheet";
import { decisionIdOf, readDecision } from "../decisionValues";
import { traceReasonOf } from "../../api/riskFields";
import { engineSentence, leadOf, noTileWords, rateMoveOf, readingsOf, type Action, type RateKpi } from "./ratePositionModel";

const DECISION = "treasury.ratePosition";
const PLAIN: readonly (readonly [RegExp, string, string])[] = [
  [/pay-fixed/g, "Pay-fixed", "A swap where the bank pays a fixed rate and receives the floating one: it gains when rates rise."],
  [/receive-fixed/g, "Receive-fixed", "A swap where the bank receives a fixed rate and pays the floating one: it gains when rates fall."],
  [/the cap\b/g, "The cap", "The most swaps the bank may hold: a share of its total assets."],
];

/** The engine's sentence with each swap word given its plain popover. */
function Words({ text }: { readonly text: string }) {
  const hits = PLAIN.flatMap(([re, label, plain]) => [...text.matchAll(re)].map((m) => ({ at: m.index ?? 0, len: m[0].length, label, plain }))).sort((a, b) => a.at - b.at);
  const parts: ReactNode[] = [];
  let at = 0;
  hits.forEach((h, i) => { if (h.at < at) return; parts.push(<Fragment key={`t${i}`}>{text.slice(at, h.at)}</Fragment>, <GxDescribed key={i} label={h.label} plain={h.plain}>{text.slice(h.at, h.at + h.len)}</GxDescribed>); at = h.at + h.len; });
  parts.push(<Fragment key="end">{text.slice(at)}</Fragment>);
  return <>{parts}</>;
}

const KPI_NAME: Readonly<Record<RateKpi, string>> = { eve200: "equity value", niiSensitivity: "income" };
const near = (v: number): string => formatRate(v, 1);

function actionText(a: Action, securities: string, valueFlagged: boolean): string {
  const shorter = valueFlagged ? ` Buying shorter bonds in ${securities} also lowers this risk.` : "";
  if (a.kind === "other") return `No tile can be shown to clear this from here.${shorter}`;
  if (a.kind === "higher") return `A higher target should help: pick a tile to the right of the one you chose, and check the figures above.${shorter}`;
  const figures = (Object.keys(a.after) as RateKpi[]).map((k) => `${KPI_NAME[k]} to about ${near(a.after[k] as number)} (limit ${near(a.limits[k] as number)})`).join(", and ");
  if (a.kind === "pick") return a.measured === true ? `Pick the ${a.toLabel} tile. That brings ${figures}.` : `Pick the ${a.toLabel} tile. That should bring ${figures}. This is an estimate; the figures above show the real result once you pick.`;
  if (a.measured !== true) return `No tile clears this: the best, ${a.bestLabel}, should only bring ${figures}.${shorter}`;
  const { at, why } = noTileWords(a);
  return `No tile clears this${at}: the best, ${a.bestLabel}, brings ${figures}. ${why}${shorter}`.replace(/ +$/, "").replace(/\. \./g, ".");
}

export function RatePositionBlock({ trace }: { readonly trace: PreviewTrace | null }) {
  const sheet = useDecisionSheet();
  if (sheet === null || sheet.projection === undefined) return null;
  const { team, catalog, projection } = sheet;
  const entry = catalog.decisions.find((d) => d.decisionId === DECISION);
  const readings = readingsOf(projection);
  if (entry === undefined || readings.length === 0) return null;
  const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
  const now = num(readDecision(team.draft?.decisions ?? {}, DECISION, "target"));
  const own = (trace?.decisions ?? []).filter((d) => decisionIdOf(d.decisionId) === DECISION);
  const move = rateMoveOf({ projection, catalog, decisions: team.draft?.decisions ?? {}, seedDecisions: team.draft?.seedDecisions ?? {}, trace });
  const action = move?.action ?? null, current = move?.current ?? false;
  const reason = own.map((d) => engineSentence(traceReasonOf(d))).find((r) => r !== null && !r.startsWith("This applies to the new swap"));
  const lead = leadOf(now ?? readDecision(team.draft?.decisions ?? {}, DECISION, "target") as string | undefined, readDecision(team.draft?.seedDecisions ?? {}, DECISION, "target"));
  const securities = catalog.decisions.find((d) => d.decisionId === "treasury.securitiesMix")?.label ?? "the securities decision";
  return (
    <div className="gx-rp" data-testid="decide.rp">
      <h3 className="gx-eyebrow">Where this leaves you</h3>
      {readings.map((r) => (
        <p key={r.kpi} className="gx-rp-reading" data-testid={`decide.rp.${r.kpi}`} data-flag={r.flagged}>
          {r.before}<GxTerm term={r.kpi === "eve200" ? "eve200" : "nii-sensitivity"}>{r.term}</GxTerm>{r.after}
          {" "}<GxPill tone={r.flagged ? "warn" : "good"} testId={`decide.rp.${r.kpi}.chip`}>{r.chip}</GxPill>
        </p>
      ))}
      {action === null ? null : <p className="gx-rp-do" data-testid="decide.rp.action"><b>What you can do.</b> {current || action.kind === "other" || action.kind === "higher" ? actionText(action, securities, readings.some((r) => r.flagged && r.kpi === "eve200")) : "Working out the move…"}</p>}
      {lead === null && reason === undefined ? null : (
        <>
          <h3 className="gx-eyebrow">What this does</h3>
          <p className="gx-small" data-testid="decide.rp.does">{lead}{lead !== null && reason !== undefined && reason !== null ? " " : ""}{reason === undefined || reason === null ? null : <Words text={reason} />}</p>
        </>
      )}
    </div>
  );
}
